import { useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import NiceModal from '@ebay/nice-modal-react';
import AOS from 'aos';

// Import utilities
import useAppResources from './useAppResources';
import useAppTemplate from './useAppTemplate';

// Import components
import PortalUnavailable from 'components/views/PortalUnavailable';
import PushNotifications from './PushNotifications';

// Import helpers
import { saveAffiliateClientData } from 'helpers/affiliate_client';
import { getSearchParametr } from 'helpers';

// Import variables
import { AFFILIATE_CLIENT } from 'helpers/variables';

// Import hooks
import { useAppSelector } from 'store/hooks';
import { useClearData } from 'components/utilities';
import { useUpdateCurrentTime } from 'components/utilities/hooks/clock/useUpdateCurrentTime';

// Import styles
import { Container } from './styles';

type Props = {
	children: ReactNode;
};

const AppTemplate = ({ children }: Props) => {
	const { i18n } = useTranslation();
	const { search } = useLocation();
	const isPortalAvailable = useAppSelector(({ portal }) => portal.isPortalAvailable);

	useUpdateCurrentTime();

	useClearData();

	useAppResources();

	const { isAuth } = useAppTemplate();

	// Set app current language
	document.documentElement.lang = i18n.language;

	useEffect(() => {
		// Save affiliate client data locally
		const affiliateID = getSearchParametr(search, AFFILIATE_CLIENT);
		affiliateID && saveAffiliateClientData(affiliateID);

		AOS.init({
			once: true,
			delay: 50,
			duration: 500,
			easing: 'ease-in-out'
		});
	}, []);

	if (!isPortalAvailable) return <PortalUnavailable />;

	return (
		<NiceModal.Provider>
			<Container>
				{isAuth && <PushNotifications />}
				{children}
			</Container>
		</NiceModal.Provider>
	);
};

export default AppTemplate;
